import { AudioLines, Headphones, Mic, Volume2 } from "lucide-react";
import type { AudioDeviceOption } from "../lib/audioDevices";
import type { TargetLanguage } from "../shared/languages";
import { UI_COPY } from "../shared/uiCopy";

export type AudioDeviceSettingsProps = {
  inputs: AudioDeviceOption[];
  outputs: AudioDeviceOption[];
  selectedInputId: string;
  selectedOutputId: string;
  outputSelectionSupported: boolean;
  inputLevel: number;
  disabled?: boolean;
  inputDisabled?: boolean;
  outputDisabled?: boolean;
  onInputChange(deviceId: string): void;
  onOutputChange(deviceId: string): void;
  onTestInput(): void;
  onTestOutput(): void;
};

type AudioDeviceSettingsViewProps = AudioDeviceSettingsProps & {
  language: TargetLanguage;
};

export function AudioDeviceSettings({
  language,
  inputs,
  outputs,
  selectedInputId,
  selectedOutputId,
  outputSelectionSupported,
  inputLevel,
  disabled = false,
  inputDisabled = disabled,
  outputDisabled = disabled,
  onInputChange,
  onOutputChange,
  onTestInput,
  onTestOutput,
}: AudioDeviceSettingsViewProps) {
  const copy = UI_COPY[language];

  return (
    <div className="device-grid">
      <div className="device-control">
        <label htmlFor="audio-input">
          <Mic size={16} aria-hidden="true" />
          {copy.microphone}
        </label>
        <select
          id="audio-input"
          value={selectedInputId}
          disabled={inputDisabled}
          onChange={(event) => onInputChange(event.currentTarget.value)}
        >
          {inputs.length === 0 && <option value="default">{copy.systemDefault}</option>}
          {inputs.map((device) => (
            <option value={device.deviceId} key={device.deviceId}>
              {device.label}
            </option>
          ))}
        </select>
        <div className="input-meter-row">
          <AudioLines size={15} aria-hidden="true" />
          <meter
            aria-label={copy.inputLevel}
            min={0}
            max={1}
            low={0.15}
            high={0.75}
            optimum={0.55}
            value={Math.min(1, Math.max(0, inputLevel))}
          />
        </div>
        <button className="secondary-button" type="button" disabled={inputDisabled} onClick={onTestInput}>
          <Mic size={16} aria-hidden="true" />
          {copy.testMicrophone}
        </button>
      </div>

      <div className="device-control">
        <label htmlFor="audio-output">
          <Headphones size={16} aria-hidden="true" />
          {copy.audioOutput}
        </label>
        <select
          id="audio-output"
          value={selectedOutputId}
          disabled={outputDisabled || !outputSelectionSupported}
          onChange={(event) => onOutputChange(event.currentTarget.value)}
        >
          {outputs.length === 0 && <option value="default">{copy.systemDefault}</option>}
          {outputs.map((device) => (
            <option value={device.deviceId} key={device.deviceId}>
              {device.label}
            </option>
          ))}
        </select>
        <p className="device-note">
          {outputSelectionSupported ? copy.systemOutputGuidance : copy.outputSelectionUnsupported}
        </p>
        <button className="secondary-button" type="button" disabled={outputDisabled} onClick={onTestOutput}>
          <Volume2 size={16} aria-hidden="true" />
          {copy.testAudio}
        </button>
      </div>
    </div>
  );
}
